<template>
  <v-dialog
    v-model="dialog"
    hide-overlay
    fullscreen
    persistent
    transition="dialog-bottom-transition"
  >
    <v-card>
      <v-toolbar dark color="secondary">
        <v-btn icon dark @click="$emit('closeShopListDefiniteDialog')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>
          <v-icon> receipt_long </v-icon>
          {{ $translate('MOMENT_FOR_SHOPPINGLIST') }}
          <b class="pl-2">
            {{ shoppingList.ShoppingListName }}
          </b>
        </v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>
      <v-card elevation="3" class="pa-4" style="overflow-x: hidden">
        <v-card-text style="word-break: normal" class="pt-0">
          <v-col cols="12" class="pt-0">
            <!-- Error banner -->
            <v-slide-y-transition>
              <ErrorCard
                v-if="error"
                :errorMessage="errorMessage"
                :functionName="errorToRecallFunction"
                @getDeliveryAndPickUpEvents="getDeliveryAndPickUpEvents"
                @getStartDates="getStartDates"
                @getStartBatches="getStartBatches"
                @getEndDates="getEndDates"
              />
            </v-slide-y-transition>

            <v-row justify="center">
              <v-col cols="12" class="pa-0">
                <!-- Select Delivery / takeAway -->
                <v-row justify="center" class="my-0">
                  <v-col
                    v-if="deliveryAndPickUpDataLoaded"
                    cols="12"
                    md="8"
                    lg="6"
                  >
                    <h2
                      :style="`color: ${$vuetify.theme.themes.light.primary}`"
                      class="pa-3 text-center"
                    >
                      {{ $translate('CHOOSE_DELIVERY_PICKUP_METHOD') }}
                    </h2>

                    <v-select
                      v-model="orderType"
                      :items="deliveryPickUpSelect"
                      hide-details="auto"
                      outlined
                      return-object
                      item-value="Name"
                      item-text="Name"
                      :prepend-inner-icon="
                        orderType.Type === 'Delivery'
                          ? 'mdi-truck'
                          : 'mdi-store'
                      "
                      :label="$translate('MAKE_CHOICE_FOR_DELIVERY_PICKUP')"
                      single-line
                      @change="updateDeliveryData"
                    >
                      <template v-slot:item="data">
                        <template v-if="!isObject(data.item)">
                          <v-list-item-content v-text="data.item" />
                        </template>
                        <template v-else>
                          <v-list-item-avatar>
                            <v-icon
                              v-if="data.item.Type === 'Delivery'"
                              color="secondary"
                              large
                            >
                              mdi-truck
                            </v-icon>
                            <v-icon v-else color="secondary" large>
                              mdi-store
                            </v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title v-html="data.item.Name" />
                            <v-list-item-subtitle
                              v-text="getDeliveryPickupSubtitle(data.item)"
                            />
                          </v-list-item-content>
                        </template>
                      </template>
                    </v-select>

                    <!-- Delivery options section -->
                    <v-expand-transition>
                      <section
                        v-if="isDelivery && deliveryFeeItems.length > 0"
                        class="mt-2 mb-3 px-2"
                      >
                        <!-- Delivery fee option in case of only 1 delivery available -->
                        <div
                          v-if="deliveryFeeItems.length === 1"
                          class="text-subtitle-1 text--primary"
                        >
                          {{ deliveryFeeItems[0].Name }}
                        </div>

                        <!-- Delivery fee options in case of more than 1 delivery available -->
                        <v-radio-group
                          v-else
                          v-model="selectedDeliveryFeeItem"
                          hide-details="auto"
                          column
                          class="mt-0"
                          @change="onDeliverySelectionChange"
                        >
                          <v-radio
                            v-for="(
                              deliveryFeeItem, deliveryFeeItemIndex
                            ) in deliveryFeeItems"
                            :key="deliveryFeeItemIndex"
                            :value="deliveryFeeItem"
                          >
                            <template v-slot:label>
                              <div class="text-subtitle-1 text--primary">
                                {{ deliveryFeeItem.Name }}
                              </div>
                            </template>
                          </v-radio>
                        </v-radio-group>
                      </section>
                    </v-expand-transition>

                    <v-expand-transition>
                      <DeliveryAlert
                        :alerts="deliveryAlerts"
                        :improvements="improvements"
                        class="mt-2"
                      />
                    </v-expand-transition>

                    <PickupAlert :visible="isPickup" :pickupFee="pickupFee" />
                  </v-col>
                </v-row>

                <!-- set the User details -->
                <v-row justify="center">
                  <v-col cols="12" md="8" lg="6">
                    <v-card outlined>
                      <h2
                        :style="`color: ${$vuetify.theme.themes.light.primary}`"
                        class="pa-3 text-center"
                      >
                        {{ $translate('ENTER_YOUR_PERSONAL_DETAILS') }}
                      </h2>

                      <v-row justify="center" class="px-3 px-md-16">
                        <v-form v-model="valid" ref="form">
                          <v-row justify="center" class="ma-0 mt-1">
                            <v-col cols="12" md="6">
                              <v-text-field
                                :label="$translate('FIRSTNAME')"
                                v-model="userObject.firstName"
                                required
                                :rules="textRule"
                              />
                            </v-col>
                            <v-col cols="12" md="6">
                              <v-text-field
                                :label="$translate('LASTNAME')"
                                v-model="userObject.lastName"
                                required
                                :rules="textRule"
                              />
                            </v-col>

                            <v-col v-if="isDelivery" cols="12">
                              <v-row>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :label="$translate('STREET')"
                                    v-model="userObject.street"
                                    required
                                    :rules="textRule"
                                  />
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :label="$translate('HOUSE_NUMBER')"
                                    v-model="userObject.houseNo"
                                    required
                                    :rules="textRule"
                                  />
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :label="$translate('POSTAL_CODE')"
                                    v-model="userObject.postalCode"
                                    :rules="postalCodeRules"
                                    @change="getDeliveryDetails"
                                  />
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :label="$translate('CITY')"
                                    v-model="userObject.city"
                                    :rules="textRule"
                                  />
                                </v-col>

                                <v-col cols="12" md="6">
                                  <v-select
                                    v-model="userObject.countryCode"
                                    :label="$translate('COUNTRY')"
                                    :rules="textRule"
                                    :items="countryCodes"
                                    @change="getDeliveryDetails"
                                  >
                                    <template slot="item" slot-scope="data">
                                      {{
                                        getCountryName(data.item, {
                                          select: 'official',
                                        })
                                      }}
                                    </template>
                                    <template
                                      slot="selection"
                                      slot-scope="data"
                                    >
                                      {{
                                        getCountryName(data.item, {
                                          select: 'official',
                                        })
                                      }}
                                    </template>
                                  </v-select>
                                </v-col>
                              </v-row>
                            </v-col>

                            <v-col cols="12" md="6">
                              <v-text-field
                                v-if="$store.getters['auth/userName'] === ''"
                                :label="$translate('EMAIL')"
                                v-model="userObject.email"
                                :rules="emailRules"
                              />
                              <v-text-field
                                v-else
                                disabled
                                :label="$translate('EMAIL')"
                                v-model="userObject.email"
                                :rules="emailRules"
                              />
                            </v-col>
                            <v-col
                              cols="12"
                              md="6"
                              v-if="$store.getters['auth/userName'] === ''"
                            >
                              <v-text-field
                                :label="$translate('CONFIRM_EMAIL')"
                                v-model="confirmEmail"
                                :rules="confirmEmailRules"
                              />
                            </v-col>
                            <v-col cols="6" md="3">
                              <v-select
                                v-model="selectedCountryCode"
                                :items="countryCallingCodes"
                                :label="$translate('COUNTRY_CODE')"
                                item-text="name"
                                item-value="callingCode"
                                :rules="phoneNumberRules"
                              ></v-select>
                            </v-col>
                            <v-col cols="6" md="3">
                              <v-text-field
                                :label="$translate('PHONE_NR')"
                                v-model="nationalPhoneNo"
                                :rules="phoneNumberRules"
                              />
                            </v-col>

                            <v-col cols="12">
                              <v-textarea
                                :label="$translate('REMARK_OPTIONAL')"
                                v-model="remarks"
                              />
                            </v-col>
                          </v-row>
                        </v-form>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Select the date(s) and time(s)-->
                <v-row justify="center">
                  <v-col cols="12" md="8" lg="6">
                    <v-card outlined>
                      <h2
                        :style="`color: ${$vuetify.theme.themes.light.primary}`"
                        class="text-center pa-3"
                      >
                        {{ $translate('PICK_YOUR_DATE_AND_TIME') }}
                      </h2>

                      <v-row justify="center" class="px-4 px-md-16 mt-1">
                        <!-- Reminder for international delivery -->
                        <v-col
                          v-if="deliveryNote !== '' && isDelivery"
                          cols="12"
                        >
                          <v-alert type="info" class="mb-0">
                            {{ deliveryNote }}
                          </v-alert>
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-menu
                            ref="menuStartDate"
                            v-model="menuStartDate"
                            :close-on-content-click="false"
                            :return-value.sync="orderDetails.StartDate"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                            :disabled="disableDateTime"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <p class="font-weight-bold mb-0">
                                {{ $translate('DATE') }}
                              </p>
                              <v-text-field
                                :disabled="disableDateTime"
                                v-model="computedDateFormattedStartDate"
                                v-bind="attrs"
                                v-on="on"
                                readonly
                                ref="menuStartDate"
                                append-icon="today"
                              />
                            </template>
                            <v-date-picker
                              :disabled="disableDateTime"
                              v-model="startDate"
                              no-title
                              @change="getStartBatches"
                              event-color="primary"
                              :allowed-dates="allowedStartDates"
                              :events="allowedStartDates"
                              @input="
                                $refs.menuStartDate.save(orderDetails.StartDate)
                              "
                              scrollable
                            />
                          </v-menu>
                        </v-col>
                        <v-col cols="12" md="6">
                          <p class="font-weight-bold mb-0">
                            {{ $translate('TIME') }}
                          </p>
                          <v-select
                            v-model="orderDetails.StartTime"
                            :items="listOfStartTimes"
                            @change="getEndDates"
                            :disabled="disableDateTime"
                          >
                            <template slot="selection" slot-scope="data">
                              {{ data.item.StartTime }}
                              <!-- - {{ data.item.EndTime }} -->
                            </template>
                            <template slot="item" slot-scope="data">
                              <template>
                                <v-row justify="space-between" class="py-1">
                                  {{
                                    data.item.StartTime +
                                    ' - ' +
                                    data.item.EndTime
                                  }}
                                  <div class="text-xs-center">
                                    <v-chip
                                      :dark="
                                        getChipData('darkModus', data.item)
                                      "
                                      :style="`background-color: ${getChipData(
                                        'color',
                                        data.item
                                      )}`"
                                    >
                                      {{
                                        $translate(
                                          getChipData('text', data.item)
                                        )
                                      }}
                                    </v-chip>
                                  </div>
                                </v-row>
                              </template>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- ticket options -->
                <v-expand-transition>
                  <v-row
                    v-if="Object.keys(listOfTickets).length > 0"
                    justify="center"
                  >
                    <v-col cols="12" md="8" lg="6">
                      <v-card outlined>
                        <v-row justify="center" class="ma-0 my-3">
                          <h2
                            :style="`color: ${$vuetify.theme.themes.light.primary}`"
                            class="pl-2 pa-sm-0"
                          >
                            {{ $translate('CHOOSE_AN_OPTION') }}
                          </h2>
                        </v-row>
                        <v-row justify="center" class="ma-0">
                          <v-col cols="11">
                            <template v-for="(item, index) in listOfTickets">
                              <v-col class="pa-0" cols="12" :key="index">
                                <v-card elevation="2" class="px-4 py-2">
                                  <v-row>
                                    <v-col cols="9">
                                      <h3>{{ item.Name }}</h3>
                                      <div
                                        v-if="item.Price != null"
                                        class="text-subtitle-2"
                                      >
                                        {{ $translate('UNIT_PRICE') }}:
                                        {{
                                          formatter.format(
                                            getTicketUnitPrice(item)
                                          )
                                        }}
                                        <span
                                          class="text-caption text--secondary"
                                        >
                                          {{ getPriceSuffix() }}
                                        </span>
                                      </div>
                                      <div v-if="isProfUser">
                                        <span class="text-body-2">
                                          {{ formatter.format(item.Price) }}
                                        </span>
                                        <span
                                          class="text-caption text--secondary"
                                        >
                                          {{ getVATText(item.VatPercentage) }}
                                        </span>
                                      </div>
                                      <div
                                        class="hidden-sm-and-down mt-2 text-body-2 text--secondary"
                                      >
                                        {{ item.Description }}
                                      </div>
                                    </v-col>
                                    <v-col cols="3" align="end">
                                      <v-row justify="start" justify-md="end">
                                        <v-btn-toggle group>
                                          <v-btn
                                            large
                                            @click="
                                              item.Amount === item.MinAmount
                                                ? (item.Amount = item.MinAmount)
                                                : item.Amount--
                                            "
                                            icon
                                          >
                                            <v-icon color="primary"
                                              >remove_circle</v-icon
                                            >
                                          </v-btn>
                                          <h2 class="ticketAmount">
                                            {{ item.Amount }}
                                          </h2>
                                          <v-btn
                                            large
                                            @click="
                                              item.Amount === item.MaxAmount
                                                ? (item.Amount = item.MaxAmount)
                                                : item.Amount++
                                            "
                                            icon
                                          >
                                            <v-icon color="primary"
                                              >add_circle</v-icon
                                            >
                                          </v-btn>
                                        </v-btn-toggle>
                                      </v-row>
                                    </v-col>
                                    <v-col class="hidden-md-and-up" cols="12">
                                      <p class="ma-0">{{ item.Description }}</p>
                                    </v-col>
                                  </v-row>
                                </v-card>
                              </v-col>
                            </template>
                          </v-col>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-expand-transition>

                <!-- repeating service -->
                <v-row justify="center">
                  <v-col cols="12" md="8" lg="6">
                    <v-card outlined>
                      <h2
                        :style="`color: ${$vuetify.theme.themes.light.primary}`"
                        class="text-center pa-3"
                      >
                        {{ $translate('AUTOMATICALLY_REPEAT_ORDER') }}
                      </h2>

                      <v-row justify="center" class="px-4 px-md-16">
                        <v-col cols="6">
                          <v-subheader class="px-0">
                            {{ $translate('REPEATING') }}
                          </v-subheader>
                        </v-col>
                        <v-col cols="6">
                          <v-select
                            return-object
                            item-text="Name"
                            :items="repeatingOptions"
                            v-model="repeatingChoice"
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-subheader class="px-0">
                            {{
                              $translate('CLEAR_SHOPPING_LIST_AFTER_PAYMENT')
                            }}
                          </v-subheader>
                        </v-col>
                        <v-col cols="6">
                          <v-switch v-model="clearShoplistAfterPayment" />
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>

              <!-- Empty order error -->
              <v-slide-y-transition>
                <v-row class="mt-3 ml-3" v-if="emptyOrderError">
                  <span class="error-message">
                    {{ $translate('EVENT_MESSAGE_SELECT_PRODUCT') }}
                  </span>
                </v-row>
              </v-slide-y-transition>

              <!-- Buttons -->
              <v-row v-if="growthVitData.hasOwnProperty('Description')">
                <v-col cols="12" md="6" offset-md="6">
                  <v-card
                    v-if="growthVitData.DiscountInfo.Type === 'Free Delivery'"
                    class="primary-font-color"
                    color="primary"
                    flat
                  >
                    <v-row class="ma-0" align="center">
                      <v-avatar class="mx-3" size="32" tile>
                        <v-icon color="white">mdi-truck</v-icon>
                      </v-avatar>
                      <v-col class="px-3 py-1">
                        <div class="text-subtitle-2">
                          {{ $translate('FREE_DELIVERY') }}
                        </div>
                        <div
                          class="text-caption"
                          v-html="growthVitamin.Description"
                        />
                      </v-col>
                    </v-row>
                  </v-card>

                  <v-card
                    v-else
                    class="primary-font-color"
                    color="primary"
                    flat
                  >
                    <v-row class="ma-0" align="center">
                      <v-avatar class="mx-3" size="32" tile>
                        <v-icon color="white">mdi-lightbulb-on</v-icon>
                      </v-avatar>
                      <v-col class="px-3 py-1">
                        <div class="text-subtitle-2">
                          {{ growthVitamin.Name }}
                        </div>
                        <div
                          class="text-caption"
                          v-html="growthVitamin.Description"
                        />
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>

              <v-row justify="center" class="my-0">
                <v-col cols="12" md="8" lg="6">
                  <DeliveryAlert
                    :alerts="deliveryAlerts"
                    :improvements="improvements"
                  />
                </v-col>
              </v-row>
            </v-row>

            <!-- Payment method -->
            <v-row justify="center">
              <v-col cols="12" md="8" lg="6" class="px-0 px-md-2">
                <v-card
                  v-if="hasMultiPaymentMethods"
                  class="mb-4 mt-4"
                  outlined
                >
                  <h2 class="text-center pa-3 primary--text">
                    {{ $translate('SELECT_PAYMENT_METHOD') }}
                  </h2>
                  <!-- Payment method selection -->
                  <PaymentMethodSelect
                    v-model="selectedPaymentMethod"
                    :items="paymentMethods"
                    :ref="paymentMethodSelectElId"
                    :id="paymentMethodSelectElId"
                  />
                </v-card>
              </v-col>
            </v-row>

            <v-row justify="center" v-if="isPickup">
              <v-col cols="12" md="8" lg="6" class="px-2">
                <PickupAlert :visible="isPickup" :pickupFee="pickupFee" />
              </v-col>
            </v-row>
          </v-col>
        </v-card-text>

        <v-row justify="center" class="mx-0">
          <v-col cols="12" md="8" lg="6">
            <v-alert type="error" v-if="createShoppingListError">
              {{ createShoppingListErrorText }}
            </v-alert>

            <v-row justify="space-between" class="mx-0 mt-2 mb-4">
              <v-btn
                color="secondary"
                class="mb-2"
                @click="$emit('closeShopListDefiniteDialog')"
              >
                {{ $translate('BACK') }}
              </v-btn>
              <v-btn
                color="primary"
                class="primary-font-color mb-2"
                :loading="loading"
                @click="confirm"
                :disabled="confirmBtnDisabled"
              >
                {{ $translate('CONFIRM') }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
      </v-card>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import doRequest from '@/api/dssRequest';
import forwardGeocoding from '@/api/Geocoding';
import { getVATText } from '@/objects/Product';
import PickupFeeJS from '@/objects/PickupFee';
import utility, { scrollToElement } from '@/shared/utility';
import { requiredRules, emailRules } from '@/shared/validations_rules';
import DeliveryAlert from '@/components/generalComponents/deliveryPickUp/DeliveryAlert';
import PickupAlert from '@/components/generalComponents/deliveryPickUp/PickupAlert';
import ErrorCard from '../ErrorHandling/ErrorCard.vue';
import PaymentMethodSelect from '@/components/booking/PaymentMethodSelect';
import PaymentMethodObj from '@/objects/PaymentMethod';
import {
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { countries } from 'countries-list';

// import countries from 'i18n-iso-countries';
let countriesCode = require('i18n-iso-countries');
countriesCode.registerLocale(require('i18n-iso-countries/langs/en.json'));
countriesCode.registerLocale(require('i18n-iso-countries/langs/nl.json'));
countriesCode.registerLocale(require('i18n-iso-countries/langs/de.json'));

export default {
  name: 'ShoppingList_Booking',
  props: {
    shoppingList: {
      type: Object,
      required: true,
      default: () => {},
    },
    changePickupDelivery: {
      type: Boolean,
    },
  },
  components: {
    ErrorCard,
    DeliveryAlert,
    PickupAlert,
    PaymentMethodSelect,
  },
  computed: {
    disableDateTime() {
      if (this.orderType.Type !== 'Delivery') return false;
      if (
        this.userObject.postalCode !== '' &&
        this.userObject.countryCode !== '' &&
        !this.deliveryError
      )
        return false;
      return true;
    },
    deliveryPickUpSelect() {
      const selectionItems = this.deliveryAndPickUpData.PickUpEvents;
      const hasPickUp =
        this.deliveryAndPickUpData.PickUpEvents != null &&
        this.deliveryAndPickUpData.PickUpEvents.length > 0;

      // add header
      if (hasPickUp) {
        selectionItems.unshift({ header: 'pickup' });
      }

      // if delivery is possible then add option
      if (this.deliveryAndPickUpData.DeliveryEvents.length !== 0) {
        if (hasPickUp) {
          selectionItems.unshift({ divider: true });
        }

        selectionItems.unshift({
          Name: this.$translate('DELIVERY'),
          Type: 'Delivery',
        });
      }
      return selectionItems;
    },
    computedDateFormattedStartDate() {
      return this.formatDate(this.startDate);
    },
    computedDateFormattedEndDate() {
      return this.formatDate(this.endDate);
    },
    ...mapGetters({
      growthVitData: 'getGrowthVitamins',
      GVTimer: 'getGVTimer',
      isProfUser: 'isProfUser',
    }),
    isDelivery() {
      if (
        this.orderType.Type === 'Delivery' ||
        this.orderType.Type === 'Shiplee'
      )
        return true;

      return false;
    },
    isPickup() {
      return this.orderType != null && this.orderType.Type === 'PickUp';
    },
    isPickupPossible() {
      return this.pickupFee != null && this.pickupFee.isPossible();
    },
    formValid() {
      // TODO: needs to be improved later via form rules
      if (this.orderType == null || Object.keys(this.orderType).length === 0) {
        return false;
      }

      if (
        this.computedDateFormattedStartDate == null ||
        this.computedDateFormattedStartDate === ''
      ) {
        return false;
      }

      if (
        this.orderDetails == null ||
        this.orderDetails.StartTime == null ||
        this.orderDetails.StartTime.StartTime == null ||
        this.orderDetails.StartTime.StartTime === ''
      ) {
        return false;
      }

      return true;
    },
    confirmBtnDisabled() {
      return (
        this.error || (this.isDelivery && this.deliveryError) || !this.formValid
      );
    },
    userId() {
      return this.$store.getters['auth/userName'];
    },
    hasMultiPaymentMethods() {
      return this.paymentMethods != null && this.paymentMethods.length > 1;
    },
  },
  watch: {
    selectedCountryCode() {
      this.validateNumber();
    },
  },
  data() {
    return {
      createShoppingListError: false,
      createShoppingListErrorText: '',
      loading: false,
      clearShoplistAfterPayment: false,
      repeatingChoice: {},
      repeatingOptions: [
        {
          Id: 4,
          Name: this.$translate('EVERY_FOUR_WEEKS'),
        },
        {
          Id: 3,
          Name: this.$translate('EVERY_THREE_WEEKS'),
        },
        {
          Id: 2,
          Name: this.$translate('EVERY_TWO_WEEKS'),
        },
        {
          Id: 1,
          Name: this.$translate('EVERY_WEEK'),
        },
        {
          Id: 0,
          Name: this.$translate('ONCE'),
        },
      ],
      confirmEmail: '',
      // rules
      textRule: [...requiredRules],
      postalCodeRules: [
        ...requiredRules,
        (v) =>
          (v != null &&
            ((/^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i.test(v.trim()) &&
              this.userObject.countryCode === 'NL') ||
              this.userObject.countryCode !== 'NL')) ||
          this.$translate('INVALID_POSTAL_CODE'),
      ],
      emailRules: [...requiredRules, ...emailRules],
      confirmEmailRules: [
        (v) =>
          (!!v && v) === this.userObject.email ||
          this.$translate('EMAIL_NO_MATCH'),
        ...emailRules,
      ],
      directDebitAlert: false,
      deliveryText: '',
      deliveryAlerts: [],
      deliveryError: null,
      successDialog: false,
      emptyOrderError: false,
      dialog: true,
      minOrderValue: '',
      deliveryAndPickUpData: {},
      deliveryAndPickUpDataLoaded: false,
      orderType: {},
      allLocations: [],
      orderDetails: {},
      possibleStartDateList: [],
      possibleEndDateList: [],
      listOfStartTimes: [],
      listOfEndTimes: [],
      listOfTickets: {},
      remarks: '',
      startDate: '',
      endDate: '',
      menuStartDate: '',
      menuEndDate: '',
      countryCodes: [],
      // ErrorCard variables
      error: false,
      errorMessage: "Couldn't get available dates...",
      errorToRecallFunction: 'getStartDates',
      // User details
      valid: null,
      userObject: this.$store.getters['auth/user'] || {},
      // Formatter
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
      addedLocale: false,
      deliveryNote: '',
      improvements: [],

      deliveryFeeItems: [],
      selectedDeliveryFeeItem: {},

      pickupFee: null,
      paymentMethods: [],
      selectedPaymentMethod: undefined,
      paymentMethodSelectElId: 'paymentMethodSelect',

      nationalPhoneNo: '',
      selectedCountryCode: '',
      countryCallingCodes: [],
      phoneNumberRules: [
        (value) =>
          !value ||
          !!this.validateNumber(value) ||
          this.$translate('INVALID_PHONE_NO'),
      ],
    };
  },
  created() {
    this.getCountryCallingCode();
    this.formatPhoneNumber();
  },
  beforeDestroy() {
    this.dialog = false;
  },
  mounted() {
    this.getDeliveryAndPickUpEvents().then(() => {
      if (this.changePickupDelivery) {
        const match = this.deliveryPickUpSelect.find((item) => {
          return item.EventId === String(this.shoppingList.SubEventId);
        });
        this.orderType = match;
         this.getStartDates(true).then(() => {
        this.startDate = this.formatDate(this.shoppingList.StartDate);

        const checkStartTime = () => {
          const match = this.listOfStartTimes.find(
            (item) => item.StartTime === this.shoppingList.StartTime
          );
          if (match) {
            this.orderDetails.StartTime = match;
            console.log(match);
          } else {
            setTimeout(checkStartTime, 1000);
          }
        };
        checkStartTime();
      });
      }
    });

    // Set default repeating option
    this.repeatingChoice = this.repeatingOptions.find(
      (option) => option.Id === 1
    );
    if (this.repeatingChoice == null) {
      [this.repeatingChoice] = this.repeatingOptions;
    }
  },
  methods: {
    formatPhoneNumber() {
      if (this.userObject.phoneNo) {
        //get only National number to display. country code will be shown in the selection
        const phoneNumber = parsePhoneNumberFromString(this.userObject.phoneNo);
        if (phoneNumber) {
          const nationalNumber = phoneNumber.nationalNumber;
          const countryCallingCode = phoneNumber.countryCallingCode;
          this.nationalPhoneNo = nationalNumber;
          this.selectedCountryCode = `+${countryCallingCode}`;
          console.log('nationalNumber', nationalNumber);
          console.log('selectedCountryCode', countryCallingCode);
        }
      } else {
        this.selectedCountryCode = '+31';
      }
    },
    getCountryCallingCode() {
      const countryCallingData = Object.values(countries);
      this.countryCallingCodes = countryCallingData.map((country) => ({
        name: country.name + ' ' + `(+${country.phone})`,
        code: country.phone,
        callingCode: `+${country.phone}`,
      }));
    },
    validateNumber() {
      if (this.selectedCountryCode && this.nationalPhoneNo) {
        let phoneNumber = this.selectedCountryCode + this.nationalPhoneNo;
        const isValid = isValidPhoneNumber(phoneNumber);
        console.log('isValid ?', isValid, phoneNumber);
        return isValid;
      }
    },
    getDeliveryAndPickUpEvents() {
      this.orderType = {};

      const payload = {
        Events: this.shoppingList.Events,
        ShoppingListId: this.shoppingList.ShoppingListId,
      };

      return new Promise((resolve, reject) => {
        doRequest(
          (response) => {
            if (
              response.data.DeliveryEvents != null &&
              response.data.DeliveryEvents.length > 0
            ) {
              this.minOrderValue =
                response.data.DeliveryEvents[0].MinimumOrderValueForDelivery;
            }

            this.deliveryAndPickUpData = response.data;
            this.countryCodes = response.data.DeliveryCountryCodes;

            const hasPickUp =
              this.deliveryAndPickUpData.PickUpEvents != null &&
              this.deliveryAndPickUpData.PickUpEvents.length > 0;
            const hasDelivery =
              this.deliveryAndPickUpData.DeliveryEvents != null &&
              this.deliveryAndPickUpData.DeliveryEvents.length > 0;

            if (!hasPickUp && !hasDelivery) {
              this.error = true;
              this.errorMessage = this.$translate('NO_DELIVERY_OR_PICKUP');
              this.errorToRecallFunction = 'getDeliveryAndPickUpEvents';
              reject(); // reject bij fout
              return;
            }

            this.deliveryAndPickUpDataLoaded = true;
            this.getStartDates();

            resolve();
          },
          () => {
            this.error = true;
            this.errorMessage = this.$translate('NO_DELIVERY_OR_PICKUP');
            this.errorToRecallFunction = 'getDeliveryAndPickUpEvents';
            reject();
          },
          'getDeliveryAndPickUpEvents',
          this.$store.getters['auth/userName'],
          payload
        );
      });
    },
    getStartDates(onDeliveryOrPickUpChange) {
      return new Promise((resolve, reject) => {
        this.error = false;
        this.possibleStartDateList = [];

        if (this.orderType == null || this.orderType.EventId == null) {
          return resolve(); // niets te doen
        }

        const toTwoDigits = (num) => (num < 10 ? `0${num}` : num);
        const today = new Date();
        const year = today.getFullYear();
        const month = toTwoDigits(today.getMonth() + 1);
        const day = toTwoDigits(today.getDate());
        const todayDate = `${day}-${month}-${year}`;

        const payload = {
          StartDate: todayDate,
          LocationId: '',
          Products: {},
          OneClickBooking: true,
          ShoppingListId: this.shoppingList.ShoppingListId,
        };

        doRequest(
          (response) => {
            this.getStartDatesSuccessfull(response.data);
            if (onDeliveryOrPickUpChange) {
              this.getPaymentMethods();
            }
            resolve();
          },
          () => {
            this.error = true;
            this.errorMessage = "Couldn't get start dates for this event...";
            this.errorToRecallFunction = 'getStartDates';
            reject();
          },
          'getStartDates',
          this.orderType.EventId,
          payload
        );
      });
    },
    getStartDatesSuccessfull(dateTimes) {
      dateTimes.StartDates.forEach((date) => {
        this.possibleStartDateList.push(this.formatDate(date));
      });

      if (dateTimes.EndDates != null) {
        dateTimes.EndDates.forEach((date) => {
          this.possibleEndDateList.push(this.formatDate(date));
        });
      }
      if (dateTimes.StartTimes != null) {
        this.listOfStartTimes = dateTimes.StartTimes;
      }

      if (dateTimes.EndTimes != null) {
        this.listOfEndTimes = dateTimes.EndTimes;
      }

      if (dateTimes.Tickets != null) {
        this.listOfTickets = dateTimes.Tickets;
      }

      this.orderDetails.StartDate = this.formatDate(dateTimes.DefaultStartDate);
      this.startDate = this.orderDetails.StartDate;
      this.orderDetails.EndDate = this.formatDate(dateTimes.DefaultEndDate);
      this.endDate = this.orderDetails.EndDate;
      this.orderDetails.StartTime = dateTimes.DefaultStartTime;
      this.orderDetails.EndTime = dateTimes.DefaultEndTime;
    },
    formatDate(date) {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      return `${day}-${month}-${year}`;
    },
    updateDeliveryData() {
      // check if orderType is delivery
      if (this.orderType == null || Object.keys(this.orderType).length === 0) {
        return;
      }

      this.selectedPaymentMethod = undefined;

      if (this.isDelivery) {
        this.getDeliveryDetails(true);
      } else {
        // remove deliveryText to remove the alert
        this.deliveryError = false;
        this.deliveryText = '';
        this.deliveryAlerts = [];
        this.getStartDates(true);
      }

      this.getPickupFee();
    },
    allowedStartDates(val) {
      return this.possibleStartDateList.indexOf(val) !== -1;
    },
    allowedEndDate(val) {
      return this.possibleEndDateList.indexOf(val) !== -1;
    },
    getStartBatches() {
      this.error = false;
      const payload = {
        StartDate: this.computedDateFormattedStartDate,
        Products: {},
        OneClickBooking: true,
        ShoppingListId: this.shoppingList.ShoppingListId,
      };

      if (this.orderDetails.Location != null) {
        payload.LocationId = this.orderDetails.Location.LocationId;
      }
      doRequest(
        (response) => {
          this.listOfStartTimes = [];
          this.possibleEndDateList = [];
          this.listOfEndTimes = [];
          if (response.data.StartTimes != null) {
            this.listOfStartTimes = response.data.StartTimes;
            this.orderDetails.StartTime = response.data.DefaultStartTime;
          }
          if (response.data.EndTimes != null) {
            this.listOfEndTimes = response.data.EndTimes;
            this.orderDetails.EndTime = response.data.DefaultEndTime;
          }
          if (response.data.EndDates != null) {
            response.data.EndDates.forEach((date) => {
              this.possibleEndDateList.push(this.formatDate(date));
            });
            this.orderDetails.EndDate = this.formatDate(
              response.data.DefaultEndDate
            );
            this.endDate = this.orderDetails.EndDate;
          }
          if (response.data.EndDates != null) {
            this.listOfTickets = response.data.Tickets;
          }
        },
        () => {
          this.error = true;
          this.errorMessage = "Couldn't get start times for this event...";
          this.errorToRecallFunction = 'getStartBatches';
        },
        'getStartBatches',
        this.orderType.EventId,
        payload
      );
    },
    getEndDates() {
      this.error = false;
      this.possibleEndDateList = [];
      const payload = {
        PartnerEventId: this.orderType.EventId,
        orderType: this.orderType,
        StartDate: this.computedDateFormattedStartDate,
        StartTime: this.orderDetails.StartTime.StartTime,
        LocationId: '',
        Products: {},
        OneClickBooking: true,
        ShoppingListId: this.shoppingList.ShoppingListId,
      };
      doRequest(
        (response) => {
          this.possibleEndDateList = [];
          if (response.data.EndDates != null) {
            response.data.EndDates.forEach((date) => {
              this.possibleEndDateList.push(this.formatDate(date));
            });
            this.listOfEndTimes = response.data.EndTimes;
            this.listOfTickets = response.data.Tickets;
            this.orderDetails.EndDate = this.formatDate(
              response.data.DefaultEndDate
            );
            this.endDate = this.orderDetails.EndDate;
            this.orderDetails.EndTime = response.data.DefaultEndTime;
          }
        },
        () => {
          this.error = true;
          this.errorMessage = "Couldn't get end dates for this event...";
          this.errorToRecallFunction = 'getEndDates';
        },
        'getEndDates',
        this.orderType.EventId,
        payload
      );
    },
    getChipData(sendedInfo, item) {
      switch (sendedInfo) {
        case 'text':
          if (item.Occupancy < 0.25) return 'NOT_BUSY'; // green
          if (item.Occupancy < 0.5) return 'NORMAL'; // yellow
          return 'ALMOST_FULL'; // red
        case 'color':
          if (item.State === 0) return 'rgba(25, 181, 254, 1)'; // blue
          if (item.Occupancy < 0.25) return 'rgba(67, 160, 71, 1)'; // green
          if (item.Occupancy < 0.5) return 'rgba(240, 255, 0, 0.8)'; // yellow
          return 'rgba(240, 52, 52, 0.8)'; // red
        case 'darkModus':
          if (item.Occupancy < 0.25) return true;
          if (item.Occupancy < 0.5) return false;
          return true;
        default:
          return false;
      }
    },
    goHome() {
      this.$router.push({
        name: 'Homepage',
        params: {
          account_id: this.$store.getters.getRegion,
        },
      });
    },
    confirm() {
      if (this.changePickupDelivery === true) {
        this.changeDeliveryPickup();
      } else {
        this.createShoppingList();
      }
    },
    changeDeliveryPickup() {
      if (!this.validatePaymentMethod()) {
        this.scrollToPaymentMethod();
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
        return;
      }

      this.loading = true;
      // if form is not valid stop the function
      if (!this.$refs.form.validate()) {
        this.loading = false;
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
        return;
      }
      const payload = {
        ShoppingListId: this.shoppingList.ShoppingListId,
        SubEventId: this.orderType.EventId,
        StartDate: this.computedDateFormattedStartDate,
        StartTime: this.orderDetails.StartTime.StartTime,
        StartBatchGroupId: this.orderDetails.StartTime.BatchGroupId,
        UserInfo: this.getUserInfo(),
        Tickets: {},
        SpecialRequests: [],
        Note: this.remarks,
        EmptyShoppingListAfterOrder: this.clearShoplistAfterPayment,
        Frequency: this.repeatingChoice.Id,
        PaymentMethod: this.selectedPaymentMethod,
      };
      for (const item of Object.values(this.listOfTickets)) {
        if (item.Amount > 0) payload.Tickets[item.Id] = item.Amount;
      }
      doRequest(
        (response) => {
          this.loading = false;
          if (response.data.RequestSuccess) {
            this.$store.commit('setCurrentSelectedShoppingList', {});
            this.$store.commit(
              'setCurrentSelectedShoppingList',
              response.data.ShoppingList
            );
          }

          this.$emit('openSuccessDefiniteDialog');
          this.$emit('closeShopListDefiniteDialog');
        },
        (error) => {
          this.createShoppingListError = true;
          this.createShoppingListErrorText = error.data.ErrorMsg;
          this.loading = false;
        },
        'changeDeliveryPickup',
        this.$store.getters['auth/userName'],
        payload
      );
    },
    createShoppingList() {
      if (!this.validatePaymentMethod()) {
        this.scrollToPaymentMethod();
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
        return;
      }

      this.loading = true;
      // if form is not valid stop the function
      if (!this.$refs.form.validate()) {
        this.loading = false;
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
        return;
      }
      const payload = {
        ShoppingListId: this.shoppingList.ShoppingListId,
        SubEventId: this.orderType.EventId,
        StartDate: this.computedDateFormattedStartDate,
        StartTime: this.orderDetails.StartTime.StartTime,
        StartBatchGroupId: this.orderDetails.StartTime.BatchGroupId,
        UserInfo: this.getUserInfo(),
        Tickets: {},
        SpecialRequests: [],
        Note: this.remarks,
        EmptyShoppingListAfterOrder: this.clearShoplistAfterPayment,
        Frequency: this.repeatingChoice.Id,
        PaymentMethod: this.selectedPaymentMethod,
      };
      for (const item of Object.values(this.listOfTickets)) {
        if (item.Amount > 0) payload.Tickets[item.Id] = item.Amount;
      }
      doRequest(
        (response) => {
          this.loading = false;
          if (response.data.RequestSuccess) {
            this.$store.commit('setCurrentSelectedShoppingList', {});
            this.$store.commit(
              'setCurrentSelectedShoppingList',
              response.data.ShoppingList
            );
          }

          this.$emit('openSuccessDefiniteDialog');
          this.$emit('closeShopListDefiniteDialog');
        },
        (error) => {
          this.createShoppingListError = true;
          this.createShoppingListErrorText = error.data.ErrorMsg;
          this.loading = false;
        },
        'createShoppingList',
        this.$store.getters['auth/userName'],
        payload
      );
    },
    getUserInfo() {
      if (this.nationalPhoneNo !== '') {
        this.userObject.phoneNo =
          this.selectedCountryCode + this.nationalPhoneNo;
      } else this.userObject.phoneNo = '';

      return {
        Name: `${this.userObject.firstName} ${this.userObject.lastName}` || '',
        Email: this.userObject.email || '',
        PhoneNumber: this.userObject.phoneNo || '',
        Street: this.userObject.street || '',
        Nr: this.userObject.houseNo || '',
        PostalCode: this.userObject.postalCode || '',
        City: this.userObject.city || '',
        Country: this.userObject.country || '',
        CountryCode: this.userObject.countryCode || '',
        Latitude:
          this.userObject.latitude == null ? -200 : this.userObject.latitude,
        Longitude:
          this.userObject.longitude == null ? -200 : this.userObject.longitude,
      };
    },
    isDeliveryAddressValid() {
      if (
        this.userObject.houseNo == null ||
        this.userObject.houseNo.trim() === ''
      ) {
        return false;
      }
      if (
        this.userObject.street == null ||
        this.userObject.street.trim() === ''
      ) {
        return false;
      }
      if (
        this.userObject.postalCode == null ||
        this.userObject.postalCode.trim() === ''
      ) {
        return false;
      }
      if (this.userObject.city == null || this.userObject.city.trim() === '') {
        return false;
      }
      return true;
    },
    geocodeAddress() {
      // Get gps location based on user's address info if Delivery needed
      if (this.userObject.latitude == null) {
        this.userObject.latitude = -200;
      }
      if (this.userObject.longitude == null) {
        this.userObject.longitude = -200;
      }

      if (!this.isDeliveryAddressValid()) {
        return;
      }

      // Start geocoding
      const userAddressObj = this.getUserInfo();
      forwardGeocoding(
        userAddressObj,
        (result) => {
          // Geocoding finished successfully
          if (result.Latitude != null) {
            this.userObject.latitude = result.Latitude;
          }
          if (result.Longitude != null) {
            this.userObject.longitude = result.Longitude;
          }
        },
        () => {
          // Geocoding finished with error
        }
      );
    },
    getCountryName(code, selection) {
      let locale = '';
      switch (Number(this.$store.getters['auth/userLanguage'].LanguageId)) {
        case 13:
          locale = 'en';
          break;
        case 14:
          locale = 'nl';
          break;
        case 17:
          locale = 'de';
          break;
        default:
          locale = 'en';
      }

      return countriesCode.getName(code, locale, selection);
    },
    getDeliveryDetails(onDeliveryOrPickUpChange) {
      this.deliveryFeeItems = [];
      this.selectedDeliveryFeeItem = {};
      this.clearDeliveryFeeDetails();
      if (!this.userObject.postalCode) {
        return;
      }

      const todayDate = utility.getCurrentDateDMY();

      const payload = {
        UserInfo: this.getUserInfo(),
        StartDate: todayDate,
        PostalCode: this.userObject.postalCode,
        Country: this.userObject.countryCode,
        Reservations: {},
        OneClickBooking: true,
        ShoppingListId: this.shoppingList.ShoppingListId,
      };

      const cartevent = this.$store.getters['shoppingCart/orders'];

      if (cartevent != null) {
        cartevent.forEach((reservation) => {
          payload.Reservations[reservation.EventId] = [reservation.OrderId];
        });
      }

      doRequest(
        (response) => {
          if (response.data.DeliveryFees != null) {
            this.deliveryFeeItems = [...response.data.DeliveryFees];
          }
          if (this.deliveryFeeItems.length > 0) {
            this.selectedDeliveryFeeItem = { ...this.deliveryFeeItems[0] };
          }
          this.onDeliverySelectionChange(onDeliveryOrPickUpChange);
        },
        () => {
          this.deliveryText = this.$translate('NO_DELIVERY_FOR_POSTAL_CODE');
          this.deliveryError = true;
          this.deliveryAlerts = [
            {
              type: 'error',
              text: this.deliveryText,
            },
          ];
        },
        'getDeliveryDetailsV2',
        this.$store.getters['auth/userObjectId'],
        payload
      );
    },
    getGeocodeLocation() {
      if (this.userObject.countryCode !== 'NL') return;

      if (
        this.userObject.postalCode.length !== 0 &&
        this.userObject.houseNo.length !== 0
      ) {
        this.geocodeAddress();
      }
    },
    getTicketUnitPrice(ticket) {
      if (this.isProfUser) {
        return ticket.Price - ticket.VatPrice;
      }
      return ticket.Price;
    },
    getPriceSuffix() {
      if (this.isProfUser) {
        return this.$translate('EXCL_VAT');
      }
      return '';
    },
    getVATText,
    getDeliveryFeeText(deliveryDetails) {
      let deliveryPrice = 0;
      let priceSuffix = '';
      if (this.isProfUser) {
        deliveryPrice =
          deliveryDetails.ResultSet.Price - deliveryDetails.ResultSet.VatPrice;
        priceSuffix = ` (${this.$translate('EXCL_VAT')})`;
      } else {
        deliveryPrice = deliveryDetails.ResultSet.Price;
      }

      return `${this.$translate('DELIVERY_FEE')}: ${this.formatter.format(
        deliveryPrice
      )}${priceSuffix}`;
    },
    clearDeliveryFeeDetails() {
      this.deliveryNote = '';
      this.improvements = [];
      this.possibleStartDateList = [];
      this.startDate = '';
      this.listOfStartTimes = [];
      this.listOfTickets = {};
    },
    onDeliverySelectionChange(onDeliveryOrPickUpChange) {
      this.clearDeliveryFeeDetails();
      this.parseDeliveryFeeDetails(onDeliveryOrPickUpChange);
    },
    parseDeliveryFeeDetails(onDeliveryOrPickUpChange) {
      if (this.selectedDeliveryFeeItem.DeliveryPossible) {
        this.deliveryText = this.getDeliveryFeeText(
          this.selectedDeliveryFeeItem
        );
        this.deliveryError = false;
        this.deliveryAlerts = [
          {
            type: 'info',
            text: this.deliveryText,
          },
        ];
        this.orderType.EventId = this.selectedDeliveryFeeItem.EventId;
        this.deliveryNote = this.selectedDeliveryFeeItem.Note;
        this.improvements = this.selectedDeliveryFeeItem.ResultSet.Improvements;
        this.getStartDatesSuccessfull(
          this.selectedDeliveryFeeItem.DateTimeInfo
        );
        if (onDeliveryOrPickUpChange) {
          this.getPaymentMethods();
        }
      } else {
        if (
          this.selectedDeliveryFeeItem.ResultSet != null &&
          this.selectedDeliveryFeeItem.ResultSet.AdditonalOrderValueForDelivery
        ) {
          this.deliveryText = this.$translate(
            'DELIVERY_NO_ENOUGH_PRODUCTS'
          ).replace(
            'price',
            this.formatter.format(
              this.selectedDeliveryFeeItem.ResultSet
                .AdditonalOrderValueForDelivery
            )
          );
        } else {
          this.deliveryText = this.$translate('NO_DELIVERY_FOR_POSTAL_CODE');
        }
        this.deliveryError = true;
        this.deliveryAlerts = [
          {
            type: 'error',
            text: this.deliveryText,
          },
        ];
      }
    },
    isObject(item) {
      return typeof item === 'object';
    },
    getDeliveryPickupSubtitle(item) {
      if (item.Type !== 'PickUp' || item.PickUpFee == null) {
        return '';
      }

      const pickupFeeObj = new PickupFeeJS(item.EventId);
      pickupFeeObj.updateWithRawData(item.PickUpFee);
      return pickupFeeObj.orderValueInfoText();
    },
    getPickupFee() {
      if (!this.isPickup) {
        this.pickupFee = null;
        return;
      }

      this.pickupFee = new PickupFeeJS(this.orderType.EventId);
      this.pickupFee.load({}, this.shoppingList.ShoppingListId);
    },
    getPaymentMethods() {
      const deliveryPickupEventId = this.orderType.EventId;

      PaymentMethodObj.getPaymentMethods(
        this.userId,
        {},
        deliveryPickupEventId,
        this.shoppingList.ShoppingListId,
        [],
        this.changePickupDelivery
      )
        .then((methods) => {
          this.paymentMethods = [...methods];
          if (this.paymentMethods.length === 1) {
            [this.selectedPaymentMethod] = this.paymentMethods;
          }
        })
        .catch(() => {
          console.log('An error occurred in getPaymentMethods');
          this.paymentMethods = [];
        });
    },
    validatePaymentMethod() {
      const paymentRef = this.$refs[this.paymentMethodSelectElId];
      console.log(paymentRef);
      if (paymentRef != null && !paymentRef.validate()) {
        return false;
      }

      return true;
    },
    scrollToPaymentMethod() {
      scrollToElement(`#${this.paymentMethodSelectElId}`);
    },
  },
};
</script>

<style lang="scss" scoped>
.ticketAmount {
  align-self: center;
}
</style>
